import { Star } from 'lucide-react';
import { ProductReview } from '@/types/products';
import Image from 'next/image';
import { useState } from 'react';
import { ImageModal } from './ImageModal';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';

interface ProductReviewsProps {
  reviews: ProductReview[];
  averageRating?: number;
  reviewCount: number;
}

export function ProductReviews({ reviews, averageRating, reviewCount }: ProductReviewsProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [selectedReviewImages, setSelectedReviewImages] = useState<string[]>([]);
  const { language } = useLanguage();
  const t = useTranslation(language);

  const handleImageClick = (images: string[] = [], index: number) => {
    setSelectedReviewImages(images);
    setSelectedImageIndex(index);
  };

  if (reviewCount === 0) {
    return (
      <section>
        <h2 style={{ fontSize: 22, margin: '0 0 var(--space-4)' }}>{t('product.reviews')}</h2>
        <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>{t('review.noReviews')}</p>
      </section>
    );
  }

  return (
    <section>
      <ImageModal
        isOpen={selectedImageIndex !== null}
        onClose={() => setSelectedImageIndex(null)}
        images={selectedReviewImages}
        initialIndex={selectedImageIndex ?? 0}
      />
      <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 22, margin: 0 }}>{t('product.reviews')}</h2>
        {averageRating !== undefined && (
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${star <= Math.round(averageRating) ? 'fill-yellow-400 text-yellow-400' : ''}`}
                  style={star <= Math.round(averageRating) ? undefined : { color: 'var(--color-neutral-400)' }}
                />
              ))}
            </div>
            <span style={{ fontSize: 14 }}>{averageRating.toFixed(1)} ({reviewCount} {t('product.reviews')})</span>
          </div>
        )}
      </div>

      <div className="flex flex-col" style={{ gap: 'var(--space-6)' }}>
        {reviews.map((review) => (
          <div key={review.id} style={{ paddingBottom: 'var(--space-4)', borderBottom: '1px solid var(--color-divider)' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
              <div>
                <p style={{ fontWeight: 600, margin: 0 }}>{review.userName}</p>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-3.5 h-3.5 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : ''}`}
                      style={star <= review.rating ? undefined : { color: 'var(--color-neutral-400)' }}
                    />
                  ))}
                </div>
              </div>
              {review.verifiedPurchase && (
                <span className="tag tag-accent-2">{t('review.verified')}</span>
              )}
            </div>

            <p style={{ fontWeight: 600, fontSize: 16, margin: '0 0 4px' }}>{review.title}</p>
            <p style={{ color: 'color-mix(in srgb, var(--color-text) 78%, transparent)', margin: '0 0 var(--space-3)' }}>{review.comment}</p>

            {review.images && review.images.length > 0 && (
              <div className="flex" style={{ gap: 8, marginBottom: 'var(--space-2)' }}>
                {review.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleImageClick(review.images, idx)}
                    className="hover:opacity-90 transition-opacity"
                    style={{ width: 64, height: 64, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--color-divider)' }}
                    aria-label={`View image ${idx + 1} in full screen`}
                  >
                    <Image
                      src={img}
                      width={100}
                      height={100}
                      alt={`"${review.title}" review image ${idx + 1}`}
                      className="washed w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            <p style={{ fontSize: 12.5, color: 'color-mix(in srgb, var(--color-text) 55%, transparent)', margin: 0 }}>
              Reviewed on {new Date(review.createdAt).toLocaleDateString('ro-RO')}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
